import { Link, useParams } from "react-router-dom";
import { news } from "@/mocks/news";
import { works } from "@/mocks/works";
import ShareMenu from "@/components/base/ShareMenu";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = news.find((n) => n.id === id);

  useSeo({
    title: item ? `天海翼《${item.title}》全文 — 天海翼影视资讯` : "资讯详情 — 天海翼影视内容站",
    description: item ? item.excerpt : "天海翼影视资讯详情页。",
    keywords: item ? `天海翼,${item.type},天海翼资讯,影视资讯,${item.title}` : "天海翼资讯",
  });

  if (!item) {
    return (
      <section className="mx-auto max-w-[1280px] px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="没有找到这条资讯"
          description="该资讯可能已被移除或链接有误，返回资讯列表看看其他内容吧。"
          actionText="返回明星资讯"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/news");
          }}
        />
      </section>
    );
  }

  const relatedWorks = works.filter((w) => item.relatedWorkIds.includes(w.id));
  const recommended = news.filter((n) => n.id !== item.id).slice(0, 3);

  return (
    <article>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <Link to="/news" className="cursor-pointer hover:text-primary-700">明星资讯</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700 line-clamp-1">{item.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700">
              <i className="ri-price-tag-3-line" />
              {item.type}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {item.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-6 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {item.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-newspaper-line" />
                来源：{item.source}
              </span>
            </div>

            <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-secondary-100">
              <div className="aspect-[8/5] w-full">
                <img
                  src={item.cover}
                  alt={`${item.title} 配图`}
                  title={`天海翼 ${item.title}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mt-8 space-y-5 text-base leading-8 text-foreground-700">
              {item.content.map((para, index) => (
                <p key={index}>{para}</p>
              ))}
            </div>

            <div className="mt-8">
              <ShareMenu path={`/news/${item.id}`} title={`天海翼资讯《${item.title}》`} />
            </div>

            {relatedWorks.length > 0 && (
              <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-film-line text-primary-600" />
                  关联影视作品
                </h2>
                <div className="flex flex-col gap-3">
                  {relatedWorks.map((work) => (
                    <Link
                      key={work.id}
                      to={`/works/${work.id}`}
                      className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    >
                      <div className="h-20 w-14 shrink-0 overflow-hidden rounded-md bg-secondary-100">
                        <img
                          src={work.cover}
                          alt={`${work.title} 封面`}
                          title={`天海翼作品 ${work.title}`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-foreground-900 transition-colors group-hover:text-primary-700">
                          {work.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">
                          {work.year} · {work.category} · {work.status}
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-foreground-600 clamp-2">{work.summary}</p>
                      </div>
                      <i className="ri-arrow-right-line text-lg text-foreground-400" />
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-list-check-2 text-accent-500" />
                相关推荐
              </h2>
              <div className="flex flex-col gap-4">
                {recommended.map((rec, index) => (
                  <Link
                    key={rec.id}
                    to={`/news/${rec.id}`}
                    className="group flex cursor-pointer gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-secondary-100">
                      <img
                        src={rec.cover}
                        alt={`${rec.title} 配图`}
                        title={`天海翼 ${rec.title}`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <h3 className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700 line-clamp-2">
                        {rec.title}
                      </h3>
                      <span className="mt-1 text-xs text-foreground-500">{rec.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}